/**
 * lib/applicationDocuments.ts
 * ------------------------------------------------------------------
 * The paperwork an application cannot be processed without.
 *
 * PURE. A file's name, type and size in; an answer out. No Storage, no
 * Firestore — what the school will and will not accept is a policy question
 * and it should be readable, and testable, on its own.
 *
 * THE OFFICE ATTACHES THESE, NOT THE APPLICANT. The documents were always
 * required — the printed form has listed them since it was built — and the
 * office collected them by email or over a counter. What was missing was
 * anywhere to PUT them, so an application sat while its paperwork lived in
 * an inbox, and the chase is where documents got lost.
 *
 * This briefly let the parent upload their own. The school said no, and
 * they were right: it would have been the only write path in the system not
 * behind a role, and the only way for somebody with no relationship to the
 * school to put a file in its bucket. Collecting them the way the school
 * already does and attaching them here costs the office a step and costs
 * the school no exposure.
 *
 * SO THE SET IS A GATE, NOT A FORM FIELD. An application cannot be printed,
 * processed or approved until the four are on file. That is the only thing
 * that makes "required" mean anything once the requirement has moved off
 * the applicant's screen and onto a colleague's.
 *
 * The checks exist here AND in storage.rules on purpose: here so the person
 * uploading gets a sentence they can act on, there because a rule is what
 * holds regardless of which screen was used.
 * ------------------------------------------------------------------ */

export type DocumentSlot =
  | 'birthCertificate'
  | 'guardianId'
  | 'lastSchoolReport'
  | 'transferOrResidence'
  | 'other';

export interface DocumentRequirement {
  slot: DocumentSlot;
  label: string;
  /** What the office is actually looking at. */
  hint: string;
  required: boolean;
}

/**
 * What the school asks for.
 *
 * The four required ones are the school's answer, not a guess: birth
 * certificate, guardian ID, the last school report, and a transfer letter
 * or proof of residence. Everything else the printed form lists — passport
 * photographs, a residence permit for non-citizens — goes under 'other',
 * because a form that refuses to submit until a non-citizen document is
 * attached is a form a citizen cannot submit.
 */
export const DOCUMENT_REQUIREMENTS: DocumentRequirement[] = [
  {
    slot: 'birthCertificate',
    label: 'Birth certificate',
    hint: "A certified copy of the pupil's birth certificate, or their passport.",
    required: true,
  },
  {
    slot: 'guardianId',
    label: 'Parent / guardian ID',
    hint: 'Omang or passport for the parent or guardian named as primary contact.',
    required: true,
  },
  {
    slot: 'lastSchoolReport',
    label: 'Last school report',
    hint: 'The most recent report or transcript from the school last attended.',
    required: true,
  },
  {
    slot: 'transferOrResidence',
    label: 'Transfer letter or proof of residence',
    hint: 'A transfer or clearance letter from the previous school, or proof of residence.',
    required: true,
  },
  {
    slot: 'other',
    label: 'Anything else',
    hint: 'Passport photographs, a residence or work permit, or anything else the school has asked for.',
    required: false,
  },
];

/** A file that has been uploaded and attached to the application. */
export interface AttachedDocument {
  slot: DocumentSlot;
  /** The name as it was uploaded — for the office to recognise. */
  fileName: string;
  /** Where it landed in Storage. */
  storagePath: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

/* --------------------------- what is allowed --------------------------- */

export const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
] as const;

/** Eight megabytes. A phone photograph of an Omang is around three. */
export const MAX_FILE_BYTES = 8 * 1024 * 1024;

/** No more than this many files in total, required ones included. */
export const MAX_FILES = 12;

/** For the file picker, so the phone offers the camera and the PDF list. */
export const ACCEPT_ATTRIBUTE = '.pdf,.jpg,.jpeg,.png,.heic,.webp,application/pdf,image/*';

/**
 * Why this file cannot be attached, or null if it can.
 *
 * The message names the limit rather than saying "invalid file": somebody
 * on a phone with a 30MB photograph needs to know to send a smaller one,
 * not that something unspecified went wrong.
 */
export function fileProblem(
  file: { name: string; type: string; size: number },
  alreadyAttached = 0,
): string | null {
  if (!file.name?.trim()) return 'That file has no name.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_FILE_BYTES) {
    return `That file is ${describeSize(file.size)}. The largest we can take is `
      + `${describeSize(MAX_FILE_BYTES)} — please send a smaller scan or photograph.`;
  }
  /* Checked on the TYPE, not the extension. A renamed file carries its real
     type, and an extension is a claim rather than a fact. Where the browser
     reports nothing — some phones do — the extension is all there is, so it
     is the fallback rather than the primary. */
  const type = String(file.type || '').toLowerCase();
  const known = (ACCEPTED_TYPES as readonly string[]).includes(type);
  const extOk = /\.(pdf|jpe?g|png|heic|webp)$/i.test(file.name);
  if (type && !known) {
    return 'We can take a PDF or a photograph (JPG, PNG, HEIC). That file is neither.';
  }
  if (!type && !extOk) {
    return 'We can take a PDF or a photograph (JPG, PNG, HEIC). Please check the file.';
  }
  if (alreadyAttached >= MAX_FILES) {
    return `That is more than ${MAX_FILES} files. Please combine them or remove one.`;
  }
  return null;
}

/** '3.4 MB'. Rounded the way a person reads a file size, not a machine. */
export function describeSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ------------------------- is the set complete? ------------------------- */

/** The required slots still missing a file. */
export function missingRequired(attached: AttachedDocument[]): DocumentRequirement[] {
  const have = new Set(attached.map((a) => a.slot));
  return DOCUMENT_REQUIREMENTS.filter((r) => r.required && !have.has(r.slot));
}

/**
 * Why this application is not ready to be processed, or null if it is.
 *
 * The same sentence gates printing and approving, so the office is never
 * told a different story by two screens.
 */
export function documentsProblem(attached: AttachedDocument[]): string | null {
  const missing = missingRequired(attached);
  if (!missing.length) return null;
  const names = missing.map((m) => m.label);
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Still needed: ${list}.`;
}

/**
 * Where a file for this application goes.
 *
 * Keyed on the application id — the document exists by the time anybody is
 * attaching to it, because the office only does this after the application
 * has arrived.
 *
 * The stored name carries the slot and a timestamp so the office can tell
 * what a file is meant to be without opening it, and re-uploading to the
 * same slot cannot collide with what is already there. The extension comes
 * from the uploaded name but the PATH never does, so a filename containing
 * a slash cannot walk out of the folder the rules confine it to.
 */
export function storagePathFor(applicationId: string, slot: DocumentSlot, fileName: string): string {
  const ext = (/\.([A-Za-z0-9]{1,5})$/.exec(fileName)?.[1] ?? 'bin').toLowerCase();
  const stamp = Date.now().toString(36);
  return `applicationDocuments/${applicationId}/${slot}-${stamp}.${ext}`;
}
